import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { TuitionRequestInterface } from "../../interfaces/tuitionRequest.interface";
import { get, put } from "../ApiHelper";

export type TuitionRequestListType =
  | "general"
  | "specific"
  | "history"
  | "withdrawn";

interface ListPagination {
  page: number;
  limit: number;
  totalResults: number;
  totalPages: number;
}

interface TuitionRequestState {
  status: string;
  allTuitionRequests: TuitionRequestInterface[];
  specificTuitionRequests: TuitionRequestInterface[];
  generalTuitionRequests: TuitionRequestInterface[];
  historyTuitionRequests: TuitionRequestInterface[];
  withdrawnTuitionRequests: TuitionRequestInterface[];
  pagination: Record<TuitionRequestListType, ListPagination>;
}

const defaultPagination: ListPagination = {
  page: 1,
  limit: 15,
  totalResults: 0,
  totalPages: 0,
};

const initialState: TuitionRequestState = {
  status: "idle",
  allTuitionRequests: [],
  specificTuitionRequests: [],
  generalTuitionRequests: [],
  historyTuitionRequests: [],
  withdrawnTuitionRequests: [],
  pagination: {
    general: { ...defaultPagination },
    specific: { ...defaultPagination },
    history: { ...defaultPagination },
    withdrawn: { ...defaultPagination },
  },
};

export const getTuitionRequests = createAsyncThunk(
  "get-tuition-requests",
  async ({
    signedAdmin,
    listType = "specific",
    page = 1,
    limit = 15,
  }: {
    signedAdmin: { type: string; id: string };
    listType?: TuitionRequestListType;
    page?: number;
    limit?: number;
  }) => {
    const queryParams = new URLSearchParams();
    queryParams.set("type", signedAdmin.type);
    queryParams.set("adminId", signedAdmin.id);
    queryParams.set("listType", listType);
    queryParams.set("page", String(page));
    queryParams.set("limit", String(limit));

    const response = await get(
      `/admin/tuition/tuition-requests?${queryParams.toString()}`
    );
    return response;
  }
);

// Async thunk for assigning a teacher
export const assignTeacher = createAsyncThunk(
  "assign-teacher",
  async (
    {
      id,
      teacherId,
      schedule,
    }: { id: string; teacherId: string; schedule: any[] },
    { rejectWithValue }
  ) => {
    try {
      const response = await put(
        `/tuition/tuition-request/${id}/assign-teacher`,
        {
          teacherId,
          schedule,
        }
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Async thunk for changing request status
export const changeRequestStatus = createAsyncThunk(
  "change-request-status",
  async (
    {
      id,
      status,
      email,
      role,
      type,
    }: {
      id: string;
      status: string;
      email: string;
      role: string;
      type: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await put(
        `/admin/tuition/tuition-request/${id}/status`,
        {
          status,
          email,
          role,
          type,
        }
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

const TuitionRequestSlice = createSlice({
  name: "tuition-request",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getTuitionRequests.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getTuitionRequests.fulfilled, (state, action) => {
        const listType = (action.payload.listType ||
          "specific") as TuitionRequestListType;
        const page = action.payload.page || 1;
        const limit = action.payload.limit || 15;
        const totalResults = action.payload.totalResults || 0;
        const totalPages = action.payload.totalPages || 0;

        state.pagination[listType] = {
          page,
          limit,
          totalResults,
          totalPages,
        };

        if (listType === "general") {
          state.generalTuitionRequests =
            action.payload.generalTuitionRequests || [];
        } else if (listType === "specific") {
          state.specificTuitionRequests =
            action.payload.teacherSpecificTuitionRequests || [];
        } else if (listType === "history") {
          state.historyTuitionRequests =
            action.payload.historyTuitionRequests || [];
        } else if (listType === "withdrawn") {
          state.withdrawnTuitionRequests =
            action.payload.withdrawnTuitionRequests || [];
        }

        state.allTuitionRequests = [
          ...state.specificTuitionRequests,
          ...state.generalTuitionRequests,
        ];
        state.status = "success";
      })
      .addCase(getTuitionRequests.rejected, (state) => {
        state.status = "reject";
      })
      .addCase(changeRequestStatus.pending, (state) => {
        state.status = "loading";
      })
      .addCase(changeRequestStatus.fulfilled, (state) => {
        state.status = "success";
      })
      .addCase(changeRequestStatus.rejected, (state, action) => {
        console.error("Error changing request status:", action.payload);
      });
  },
});

export default TuitionRequestSlice.reducer;
